import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import * as API from '../apiConfig';

function Clients() {
    const initialClientForm = { clientName: '', phoneNumber: '', address: '' };
    const navigate = useNavigate();
    const { authData, onLogout } = useOutletContext();
    const authToken = authData?.token;
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
    const [isBillsModalOpen, setIsBillsModalOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [orders, setOrders] = useState([]);
    const [bills, setBills] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const [selectedClientIdForBill, setSelectedClientIdForBill] = useState('');
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [billClientSearch, setBillClientSearch] = useState('');
    const [clientForm, setClientForm] = useState(initialClientForm);
    const [orderClientSearch, setOrderClientSearch] = useState('');
    const [editingClientId, setEditingClientId] = useState(null);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [isSubmittingClient, setIsSubmittingClient] = useState(false);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
    const [isLoadingBills, setIsLoadingBills] = useState(false);
    const [isSubmittingBill, setIsSubmittingBill] = useState(false);
    const [isLoadingBillDetails, setIsLoadingBillDetails] = useState(false);
    const [clientMessage, setClientMessage] = useState('');
    const [clientError, setClientError] = useState('');
    const [orderMessage, setOrderMessage] = useState('');
    const [orderError, setOrderError] = useState('');
    const [billMessage, setBillMessage] = useState('');
    const [billError, setBillError] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadedUrl, setUploadedUrl] = useState(null);

    const [isScanning, setIsScanning] = useState(false);


    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editedOrder, setEditedOrder] = useState({});
    const [saving, setSaving] = useState(false);


    const handleClientSubmit = async (event) => {
        event.preventDefault();
        setIsSubmittingClient(true);
        setClientError('');
        setClientMessage('');
        const isEditing = Boolean(editingClientId);
        const requestUrl = isEditing ? `${API.CLIENTS_API_URL}/${editingClientId}` : API.CLIENTS_API_URL;
        const requestMethod = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(requestUrl, {
                method: requestMethod,
                headers: getRequestHeaders(),
                body: JSON.stringify(clientForm),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to save client');
            }
            setClientMessage(
                data.message || (isEditing ? 'Client updated successfully' : 'Client added successfully')
            );
            resetClientForm();
            await fetchClients();
        } catch (submitError) {
            setClientError(submitError.message || 'Unable to save client');
        } finally {
            setIsSubmittingClient(false);
        }
    };
    const getRequestHeaders = useCallback(() => {
        const headers = { 'Content-Type': 'application/json' };
        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        }
        return headers;
    }, [authToken]);

    const resetClientForm = () => {
        setClientForm(initialClientForm);
        setEditingClientId(null);
    };

    const fetchClients = useCallback(async () => {
        setIsLoadingClients(true);
        setClientError('');

        try {
            const response = await fetch(API.CLIENTS_API_URL, {
                headers: getRequestHeaders()
            });

            // 🔴 Handle 403 globally here
            if (response.status === 403) {
                onLogout();
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Unable to load clients');
            }

            setClients(data.clients || []);
            setClientMessage(data.message || 'Clients retrieved successfully');

        } catch (fetchError) {
            setClientError(fetchError.message || 'Unable to load clients');
        } finally {
            setIsLoadingClients(false);
        }
    }, [getRequestHeaders, onLogout]);

    const handleClientFormChange = ({ target: { name, value } }) => {
        setClientForm((current) => ({ ...current, [name]: value }));
    };

    const handleEditClient = (client) => {
        setClientError('');
        setClientMessage(`Editing ${client.clientName}`);
        setEditingClientId(client.id);
        setClientForm({
            clientName: client.clientName || '',
            phoneNumber: client.phoneNumber || '',
            address: client.address || '',
        });
    };


    const handleDeleteClient = async (clientId) => {
        setClientError('');
        setClientMessage('');
        try {
            const response = await fetch(`${API.CLIENTS_API_URL}/${clientId}`, {
                method: 'DELETE',
                headers: getRequestHeaders(),
            });
            let data = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }
            if (!response.ok) {
                throw new Error(data.message || 'Unable to delete client');
            }
            setClientMessage(data.message || 'Client deleted successfully');
            if (editingClientId === clientId) {
                resetClientForm();
            }
            await fetchClients();
        } catch (deleteError) {
            setClientError(deleteError.message || 'Unable to delete client');
        }
    };


    useEffect(() => {
        fetchClients();
    }, [fetchClients]);


    return (
        <div className="modal-body px-3 px-md-4 py-4">

            <div className="row g-4">

                {/* FORM */}
                <div className="col-12 col-lg-5">
                    <div className="border rounded-3 p-3 p-md-4 h-100 bg-light">

                        <h6 className="fw-semibold mb-3">
                            {editingClientId ? 'Update Client' : 'Add Client'}
                        </h6>

                        {clientMessage && <div className="alert alert-success py-2">{clientMessage}</div>}
                        {clientError && <div className="alert alert-danger py-2">{clientError}</div>}

                        <form onSubmit={handleClientSubmit}>

                            <div className="mb-3">
                                <label className="form-label small">Client Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="clientName"
                                    value={clientForm.clientName}
                                    onChange={handleClientFormChange}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label small">Phone</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="phoneNumber"
                                    value={clientForm.phoneNumber}
                                    onChange={handleClientFormChange}
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="form-label small">Address</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    name="address"
                                    value={clientForm.address}
                                    onChange={handleClientFormChange}
                                    required
                                />
                            </div>

                            <div className="d-grid gap-2 d-md-flex">
                                <button
                                    type="submit"
                                    className="btn btn-primary w-100"
                                    disabled={isSubmittingClient}
                                >
                                    {isSubmittingClient
                                        ? 'Saving...'
                                        : editingClientId
                                            ? 'Update'
                                            : 'Add'}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary w-100"
                                    onClick={resetClientForm}
                                >
                                    Clear
                                </button>
                            </div>

                        </form>
                    </div>
                </div>

                {/* TABLE */}
                <div className="col-12 col-lg-7">

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="fw-semibold mb-0">Clients</h6>
                            <small className="text-muted">
                                {isLoadingClients
                                    ? 'Loading...'
                                    : `${clients.length} total`}
                            </small>
                        </div>

                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={fetchClients}
                            disabled={isLoadingClients}
                        >
                            Refresh
                        </button>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">

                            <thead className="table-light">
                                <tr>
                                    <th>Name</th>
                                    <th className="d-none d-md-table-cell">Phone</th>
                                    <th className="d-none d-lg-table-cell">Address</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>

                            <tbody>

                                {!isLoadingClients && clients.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted py-4">
                                            No clients found
                                        </td>
                                    </tr>
                                )}

                                {clients.map((client) => (
                                    <tr key={client.id}>

                                        <td>
                                            <div className="fw-semibold">{client.clientName}</div>
                                            <small className="text-muted d-md-none">
                                                {client.phoneNumber}
                                            </small>
                                        </td>

                                        <td className="d-none d-md-table-cell">
                                            {client.phoneNumber}
                                        </td>

                                        <td className="d-none d-lg-table-cell text-truncate" style={{ maxWidth: "200px" }}>
                                            {client.address}
                                        </td>

                                        <td className="text-end">
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleEditClient(client)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDeleteClient(client.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>

                                    </tr>
                                ))}

                            </tbody>
                        </table>
                    </div>

                </div>
            </div>
        </div>
    );
}
export default Clients;